<template>

    <kt-modal :visible="recordVisible" :title="recordTitle" @dismiss="onDismiss()" size="modal-lg">
        <template #body>
            <mileage-form v-model="record"></mileage-form>
        </template>

        <template #footer>
            <div class="kt-modal-actions d-flex align-items-center justify-content-between w-100">
                <div class="form-check form-switch">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="sendNotificationToggleMileage"
                        v-model="sendNotification"
                    >
                    <label class="form-check-label" for="sendNotificationToggleMileage">
                        Send Update Notification
                    </label>
                </div>
                <div>
                    <button @click="onDismiss()" class="btn btn-outline-secondary">
                        <X :size="16" class="me-1" />Close
                    </button>
                    <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger ms-2">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                    <button @click="saveRecord()" class="btn btn-primary ms-2">
                        <Save :size="16" class="me-1" />Save
                    </button>
                </div>
            </div>
        </template>
    </kt-modal>


</template>


<script lang="ts" setup>

import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import MileageForm from "@/components/Mileage/MileageForm.vue";

import Emitter from "@/services/Emitter";
import { MileageModel } from "@/models/MileageModel";

import KtModal from "@/components/UI/KtModal.vue";
import { X, Trash2, Save } from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Mileage Entry");
const recordVisible = ref(true);

const props = defineProps({
    id: {
        type: [Number, String],
        default: null
    },
    requestId: {
        type: Number,
        default: 0
    },
    clientId: {
        type: Number,
        default: 0
    },
    techId: {
        type: Number,
        default: 0
    },
    embedded: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(["cancel", "saved"]);

const unwatch:Array <Function> = [];

const newRecord = (): MileageModel => 
{
    const mileage = new MileageModel();
    // Pre-populate with context if provided
    if (props.requestId) {
        mileage.RequestId = props.requestId;
    }
    if (props.clientId) {
        mileage.CustomerNumber = props.clientId;
    }
    if (props.techId) {
        mileage.TechnicianId = props.techId;
    }
    // Set default date to today
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    mileage.DateAndTime = `${year}-${month}-${day}`;
    return mileage;
};

const record = ref<MileageModel>(newRecord());
const sendNotification = ref(false);

const saveRecord = async () => {
    try {
        // TODO - validate record here
        let data = Helper.prep(record.value);

        console.debug("save", data);

        data = await ApiService.saveMileage(data);

        // reload any stores you need to ...
        // ...

        // If embedded mode or global modal mode (any context props provided), emit saved event
        const isGlobalModal = props.id !== null || props.requestId || props.clientId || props.techId;
        if (props.embedded || isGlobalModal) {
            emit("saved");
        } else {
            // redirect ... 
            Emitter.emit("redirect", "/mileage/edit/" + data.Id);
        }
    } catch (error) {
        console.error("Save failed:", error);
    }
};

const deleteRecord = async () => {
    Alert.notify("Deletion is currently disabled.");
};


const onDismiss = () => {
    // If embedded mode or global modal mode (any context props provided), emit cancel event
    const isGlobalModal = props.id !== null || props.requestId || props.clientId || props.techId;
    if (props.embedded || isGlobalModal) {
        emit("cancel");
    } else {
        router.back();
    }
};

const loadRecord = async (val?: string | number | null): Promise<void> => 
{
    // Priority: id prop > embedded mode > route params
    if (props.id !== null && props.id !== undefined) {
        // Global modal mode with id prop
        if (props.id && props.id !== "new") {
            let rec = await ApiService.loadMileage(props.id);
            console.debug("mileage", rec);
            record.value = rec;
        } else {
            record.value = newRecord();
        }
    } else if (props.embedded && props.requestId) {
        // In embedded mode, create a new mileage for the request
        let newRec = newRecord();
        newRec.RequestId = props.requestId;
        record.value = newRec;
    } else if (val) {
        // Route-based loading
        if (val === "new") {
            record.value = newRecord();
        } else {
            let rec = await ApiService.loadMileage(val);
            console.debug("mileage", rec);
            record.value = rec;
        }
    } else {
        record.value = newRecord();
    }
};

// Watch id prop for global modal usage
unwatch.push(watch(() => props.id, async (newId) => {
    if (newId !== null && newId !== undefined) {
        await loadRecord();
    }
}));

const hasContext = !!(props.requestId || props.clientId || props.techId);
if (!props.embedded && props.id === null && !hasContext) {
    // Only watch route params if not in embedded mode, no id prop, and no context props
    unwatch.push(watch(() => route.params.id, async (nv, ov) => 
    {
        // Handle both new and edit routes
        let idValue = nv;
        if (Array.isArray(idValue)) {
            idValue = idValue[0];
        }
        if (!idValue && route.name === "Mileage New") {
            loadRecord("new");
        } else if (idValue) {
            loadRecord(idValue);
        }
    }, 
    { 
        immediate: true 
    }));
} else if (hasContext && props.id === null) {
    // Global modal with context props — create new record with pre-populated fields
    record.value = newRecord();
} else if (props.id !== null) {
    // Load immediately when id prop is provided
    loadRecord();
} else if (props.embedded) {
    // Load immediately in embedded mode
    loadRecord("new");
}

onUnmounted(() =>
{
    unwatch.forEach((un) =>
    {
        un();
    });
});

</script>